/* =============================================================================
 * FilterSearchableList — shared filter primitive (KGH-795)
 * -----------------------------------------------------------------------------
 * Search box + removable selected tags + a capped, scrolling checkbox list.
 * Extracted verbatim from the athlete side's "Majors Offered" block; it is the
 * same control the coach sidebar needs for travel clubs (81 of them on staging,
 * thousands in production), so it lives here rather than being written twice.
 *
 * The cap is deliberate: rendering a thousand checkbox rows inside an
 * accordion costs a visible frame drop, and a coach looking for one club is
 * better served by being told to refine than by an endless list.
 * ========================================================================== */
import React from 'react';
import { X } from 'react-feather';
import { color as apsColor } from '../../styles/tokens';
import { FilterCheckboxGroup, FilterOption } from './FilterCheckboxGroup';
import { TextInput } from '../ui';

export interface FilterSearchableListProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  placeholder: string;
  /** The full option set; filtering by `searchValue` happens here. */
  options: Array<FilterOption | string>;
  selected: string[];
  onToggle: (value: string) => void;
  /** Shown instead of the list while the options are loading. */
  loading?: boolean;
  loadingNode?: React.ReactNode;
  /** Max rows rendered at once. */
  cap?: number;
  /** Copy for the two empty cases and the cap notice. */
  emptyText: string;
  noMatchText: string;
  capText?: (shown: number, total: number) => string;
  /** Label shown on a selected tag, when the value is not the label. */
  tagLabel?: (value: string) => string;
  ariaLabel?: string;
}

function optionText(o: FilterOption | string): string {
  if (typeof o === 'string') return o;
  return typeof o.label === 'string' ? o.label : o.value;
}

export const FilterSearchableList: React.FC<FilterSearchableListProps> = ({
  searchValue,
  onSearchChange,
  placeholder,
  options,
  selected,
  onToggle,
  loading = false,
  loadingNode = null,
  cap = 50,
  emptyText,
  noMatchText,
  capText = (shown, total) => `Showing ${shown} of ${total} results. Refine your search.`,
  tagLabel,
  ariaLabel,
}) => {
  const needle = searchValue.toLowerCase();
  const matches = options.filter((o) => optionText(o).toLowerCase().includes(needle));
  const shown = matches.slice(0, cap);

  return (
    <>
      {/* Search input */}
      <div style={{ position: 'relative', marginBottom: '8px' }}>
        {/* The box is TextInput; the clear button on top of it is what the
          * primitive has no slot for, so the wrapper and its padding stay. The
          * onFocus/onBlur border swap is gone with the inline `outline: none`
          * beside it, which outranked the global focus ring and left this box
          * drawing nothing for a keyboard user. The ring is the indicator now;
          * the border stays put, because the primitive's inline `border` beats
          * the `.aps-input:focus` rule (globals.css says so at the rule). It
          * keeps the sidebar's compact 8/12 padding, not the form-column
          * 12/14. */}
        <TextInput
          type="text"
          placeholder={placeholder}
          aria-label={placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            padding: '8px 12px',
            paddingRight: searchValue ? '28px' : '12px',
            border: '1px solid var(--aps-control-border)',
            borderRadius: '6px',
            fontSize: '13px',
          }}
        />
        {searchValue && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label="Clear search"
            style={{
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              display: 'flex',
            }}
          >
            <X size={14} color={apsColor.controlMuted} />
          </button>
        )}
      </div>

      {/* Selected values, removable */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
          {selected.map((value) => (
            /* A <span role="button"> rather than a real <button>: this tag ships
             * today on the athlete side and a UA button box would move its
             * metrics. role + tabIndex gets the house focus ring from
             * globals.css and the same keyboard behaviour. */
            <span
              key={value}
              role="button"
              tabIndex={0}
              onClick={() => onToggle(value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle(value);
                }
              }}
              aria-label={`Remove ${tagLabel ? tagLabel(value) : value}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                background: 'var(--aps-action-tint)',
                border: '1px solid var(--aps-control-tag-border)',
                color: 'var(--aps-control-tag-text)',
                fontSize: '11px',
                fontWeight: '500',
                padding: '2px 8px',
                borderRadius: '12px',
                cursor: 'pointer',
              }}
            >
              {tagLabel ? tagLabel(value) : value}
              <X size={10} aria-hidden="true" />
            </span>
          ))}
        </div>
      )}

      {/* The list */}
      <div style={{ maxHeight: '200px', overflowY: 'auto', overscrollBehavior: 'contain' }}>
        {loading ? (
          loadingNode
        ) : shown.length > 0 ? (
          <FilterCheckboxGroup options={shown} selected={selected} onToggle={onToggle} size="sm" ariaLabel={ariaLabel} />
        ) : (
          <div style={{ fontSize: '13px', color: 'var(--aps-control-muted)', padding: '8px 0' }}>
            {searchValue ? noMatchText : emptyText}
          </div>
        )}
        {!loading && matches.length > cap && (
          <div style={{ fontSize: '11px', color: 'var(--aps-control-muted)', padding: '8px 0', textAlign: 'center' }}>
            {capText(cap, matches.length)}
          </div>
        )}
      </div>
    </>
  );
};

export default FilterSearchableList;
